import React from 'react';
import { Box, Typography, Grid as MuiGrid, useTheme } from '@mui/material';
import {
  SportsSoccer,
  TrendingUp,
  TrendingDown,
  EmojiEvents,
  Group,
  Timer,
  Gavel,
  Shield,
  Star,
  Score,
  Flag,
  Person,
  Remove,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import colors from '../../design-system/colors';
import spacing from '../../design-system/spacing';
import shadows from '../../design-system/shadows';
import typography from '../../design-system/typography';

const ICON_MAP = {
  SportsSoccer,
  TrendingUp,
  TrendingDown,
  EmojiEvents,
  Group,
  Timer,
  Gavel,
  Shield,
  Star,
  Score,
  Flag,
  Person,
  Remove,
};

const DEFAULT_ICON_COLORS = [
  colors.primary[500],
  colors.info.main,
  colors.secondary[600],
  colors.success.main,
  colors.warning.main,
  colors.error.main,
];

const MiniStatCard = ({ item, index }) => {
  const theme = useTheme();
  const IconComponent = ICON_MAP[item.icon] || SportsSoccer;
  const iconColor = item.iconColor || DEFAULT_ICON_COLORS[index % DEFAULT_ICON_COLORS.length];

  const trendColor =
    item.trend > 0
      ? colors.football.win
      : item.trend < 0
        ? colors.football.loss
        : theme.palette.text.secondary;

  const TrendIcon =
    item.trend > 0 ? TrendingUp : item.trend < 0 ? TrendingDown : Remove;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
    >
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: `${spacing.borderRadius.md}px`,
          p: 1.5,
          boxShadow: shadows.card,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: shadows.cardHover,
            borderColor: colors.primary[300],
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              backgroundColor: `${iconColor}15`,
              borderRadius: `${spacing.borderRadius.md}px`,
              p: 0.6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconComponent sx={{ fontSize: 18, color: iconColor }} />
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: typography.fontSize.xs,
              lineHeight: 1.2,
              flex: 1,
            }}
          >
            {item.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, pl: 0.5 }}>
          <Typography
            sx={{
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.bold,
              color: 'text.primary',
              lineHeight: 1,
            }}
          >
            {item.value}
          </Typography>
          {item.suffix && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: typography.fontSize.xs }}
            >
              {item.suffix}
            </Typography>
          )}
          {item.trend != null && item.trend !== 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              <TrendIcon sx={{ fontSize: 14, color: trendColor }} />
              <Typography
                variant="caption"
                sx={{ color: trendColor, fontSize: '0.65rem', fontWeight: typography.fontWeight.medium }}
              >
                {Math.abs(item.trend)}%
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </motion.div>
  );
};

const StatCardRenderer = ({ data }) => {
  if (!data?.items?.length) return null;

  return (
    <Box sx={{ my: 1 }}>
      <MuiGrid container spacing={1.5}>
        {data.items.map((item, index) => (
          <MuiGrid
            key={index}
            size={{ xs: 6, sm: 6, md: 3 }}
          >
            <MiniStatCard item={item} index={index} />
          </MuiGrid>
        ))}
      </MuiGrid>
    </Box>
  );
};

export default StatCardRenderer;
